import {
    useEffect,
    useState,
} from "react"
import { isValidEmail } from "modules/Utils"
import { PRO_PLAN_ADDITIONAL_SEAT_PRICE } from "modules/Const"
import { BrandUser } from "api/brandApi"
import ModalSymphony from "elements/ModalSymphony"
import useBrand from "Hooks/useBrand"
import { toastError, toastSuccess } from "modules/Toast"
import InputSymphony from "elements/InputSymphony"
import Banner from "elements/Banner"

interface AddUserModalProps {
    open: boolean
    onClose: () => void
    handleUpdateUsers: (users: BrandUser[]) => void
    currentUserCount: number
}

const AddUserModal = ({
    open,
    onClose,
    handleUpdateUsers,
    currentUserCount
}: AddUserModalProps) => {
    const {
        addUserBrand,
        getBrandUsers,
    } = useBrand()

    const [error, setError] = useState(false)
    const [loading, setLoading] = useState(false)
    const [email, setEmail] = useState('')

    useEffect(() => {
        if (open) {
            setEmail('')
            setError(false)
        }
    }, [open])

    const renderSubscriptionChangeNotice = () => {
        // NOTE: you get 2 free seats on Pro
        if ((currentUserCount + 1) <= 2) {
            return null
        }

        return (
            <Banner
                className="deleteUserModalBanner"
                description={`Adding an additional user will increase your monthly subscription by $${PRO_PLAN_ADDITIONAL_SEAT_PRICE}`}
            />
        )
    }

    const handleAddUser = async () => {
        const sanitizedEmail = email.toLowerCase().trim()

        try {
            setLoading(true)
            await addUserBrand(sanitizedEmail)
            const { users } = await getBrandUsers()
            handleUpdateUsers(users)
        } catch (error) {
            console.error(error)
            toastError(`There was an error adding ${sanitizedEmail} to your profile - please try again.`)
        } finally {
            setLoading(false)
            onClose()
            toastSuccess(`${sanitizedEmail} has been added to your profile.`)
        }
    }

    const handleChangeEmail = (value: string) => {
        setError(value.length > 0 && !isValidEmail(value))
        setEmail(value)
    }

    return (
        <ModalSymphony
            className="addUserModalContainerModalCool"
            isOpen={open}
            onClose={onClose}
            title="Add a new user"
            subtitle="Manage the users who have access to your profile."
            style={{ gap: 16 }}
            buttonRightText="Send invite"
            buttonRightLoadingText="Sending invite..."
            onClickButtonRight={handleAddUser}
            buttonLeftText="Cancel"
            onClickButtonLeft={onClose}
            buttonRightIsLoading={loading}
            buttonRightDisabled={loading || error || !email.length}
            headerIcon="audiences"
        >
            <InputSymphony
                className="addUserModalInput"
                label="Email address"
                placeholder="teammate@example.com"
                value={email}
                onChange={(value) => handleChangeEmail(value)}
            />

            {renderSubscriptionChangeNotice()}
        </ModalSymphony>
    )
}

export default AddUserModal
